// src/utils/ReferrerManager.js - SIMPLIFIED VERSION

/**
 * Simplified utility for managing referrer-to-databuyer caching
 * Assumption: Once we cache a referrer, we don't need to handle changes during session
 */
class ReferrerManager {
  // Storage key for the referrer mapping
  static STORAGE_KEY = "ownli_referrer_mapping";

  /**
   * Gets the current referrer from URL parameters
   *
   * @returns {string|null} - Current referrer or null
   */
  static getCurrentReferrerFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("utm_referrer");
    } catch (error) {
      console.error("Error getting referrer from URL:", error);
      return null;
    }
  }

  /**
   * Gets referrer with subdomain priority: subdomain -> utm_referrer -> null
   *
   * @returns {string|null} - Referrer code or null
   */
  static getReferrerWithSubdomainFallback() {
    try {
      // Subdomain mapping
      const SUBDOMAIN_MAPPING = {
        dimo: "dimo",
        bbmc: "bbmc",
      };

      // 1. Check subdomain first
      const hostname = window.location.hostname;
      const subdomain = hostname.split(".")[0];

      if (SUBDOMAIN_MAPPING[subdomain]) {
        return SUBDOMAIN_MAPPING[subdomain];
      }

      // 2. Fallback to utm_referrer
      const params = new URLSearchParams(window.location.search);
      const utmReferrer = params.get("utm_referrer");

      if (utmReferrer) {
        return utmReferrer;
      }

      // 3. No referrer found
      return null;
    } catch (error) {
      console.error("Error getting referrer with subdomain fallback:", error);
      // Fallback to utm only if subdomain logic fails
      const params = new URLSearchParams(window.location.search);
      return params.get("utm_referrer");
    }
  }
  /**
   * Gets cached databuyer for a referrer
   *
   * @param {string} referrer - The referrer code
   * @returns {Object|null} - Cached databuyer object or null
   */
  static getCachedDataBuyer(referrer) {
    try {
      if (!referrer) return null;

      const cachedMapping = localStorage.getItem(this.STORAGE_KEY);
      if (!cachedMapping) return null;

      const mapping = JSON.parse(cachedMapping);
      const cachedData = mapping[referrer];

      if (!cachedData) return null;

      // Check if cache is expired (24 hours)
      const cacheAge = Date.now() - (cachedData.timestamp || 0);
      const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours

      if (cacheAge > MAX_CACHE_AGE) {
        console.log(`Cache expired for referrer: ${referrer}`);
        this.removeCachedDataBuyer(referrer);
        return null;
      }

      return cachedData.dataBuyer;
    } catch (error) {
      console.error("Error getting cached databuyer:", error);
      return null;
    }
  }

  /**
   * Caches databuyer for a referrer
   *
   * @param {string} referrer - The referrer code
   * @param {Object} dataBuyer - The databuyer object to cache
   */
  static setCachedDataBuyer(referrer, dataBuyer) {
    try {
      if (!referrer || !dataBuyer) return;

      const existingMapping = localStorage.getItem(this.STORAGE_KEY);
      const mapping = existingMapping ? JSON.parse(existingMapping) : {};

      mapping[referrer] = {
        dataBuyer,
        timestamp: Date.now(),
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(mapping));
      console.log(`Cached databuyer for referrer: ${referrer}`);
    } catch (error) {
      console.error("Error caching databuyer:", error);
    }
  }

  /**
   * Removes cached databuyer for a referrer
   *
   * @param {string} referrer - The referrer code
   */
  static removeCachedDataBuyer(referrer) {
    try {
      if (!referrer) return;

      const existingMapping = localStorage.getItem(this.STORAGE_KEY);
      if (!existingMapping) return;

      const mapping = JSON.parse(existingMapping);
      delete mapping[referrer];

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(mapping));
    } catch (error) {
      console.error("Error removing cached databuyer:", error);
    }
  }

  /**
   * Clears all cached referrer mappings
   */
  static clearAllCache() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log("Cleared referrer cache");
    } catch (error) {
      console.error("Error clearing referrer cache:", error);
    }
  }
}

export default ReferrerManager;
